import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Tooltip } from "@mui/material";

interface MenuItem {
	key: string;
	label: string;
	icon: React.ReactNode;
	route: string;
	children?: MenuItem[];
	tooltip?: string;
}

interface SidebarMenuItemProps {
	item: MenuItem;
	level?: number;
	isExpanded: boolean;
	isActive: boolean;
	hasActiveChild: boolean;
	isCollapsed?: boolean;
	onClick: () => void;
	toggleMenu: (key: string) => void;
	renderChildren?: (children: MenuItem[], parentKey: string) => React.ReactNode;
}

const SIDEBAR_MENU_ITEM_BASE =
	"sidebar-nav-item flex min-h-[48px] w-full items-center gap-3 px-[18px] py-3 font-sans text-[12px] font-normal transition focus:outline-none";

const sidebarMenuTooltipComponentsProps = {
	tooltip: {
		sx: {
			bgcolor: "var(--admin-foreground)",
			color: "var(--admin-primary-foreground)",
			fontSize: "0.8rem",
			fontWeight: 500,
			px: 2,
			py: 1,
			borderRadius: "10px",
			boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
			"& .MuiTooltip-arrow": {
				color: "var(--admin-foreground)",
			},
		},
	},
};

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
	item,
	level = 0,
	isExpanded,
	isActive,
	hasActiveChild,
	isCollapsed = false,
	onClick,
	renderChildren,
}) => {
	const hasChildren = Boolean(item.children?.length);
	const isItemActive = isActive || hasActiveChild;

	if (isCollapsed && level === 0) {
		return (
			<li key={item.key}>
				<Tooltip
					title={item.tooltip?.trim() || item.label}
					arrow
					placement="right"
					enterDelay={200}
					componentsProps={sidebarMenuTooltipComponentsProps}>
					<span className="flex w-full justify-center">
						<button
							type="button"
							onClick={onClick}
							className={`sidebar-nav-item flex h-11 w-11 items-center justify-center rounded-none ${
								isItemActive ? "sidebar-nav-item--active" : ""
							}`}
							aria-label={item.label}>
							<span className="sidebar-menu-icon">{item.icon}</span>
						</button>
					</span>
				</Tooltip>
			</li>
		);
	}

	if (isCollapsed && level > 0) {
		return null;
	}

	const childBase = `${SIDEBAR_MENU_ITEM_BASE} pl-8 opacity-80`;
	const childInactive = "hover:bg-white/10";
	const childActive = "sidebar-nav-item--active font-semibold opacity-100";

	const parentBase = SIDEBAR_MENU_ITEM_BASE;
	const parentActive = "sidebar-nav-item--active font-semibold";
	const parentInactive = "opacity-80 hover:bg-white/10";

	const buttonContent = (
		<button
			type="button"
			onClick={onClick}
			className={
				level > 0
					? `${childBase} ${isActive ? childActive : childInactive}`
					: `${parentBase} ${isItemActive ? parentActive : parentInactive}`
			}>
			<span className="sidebar-menu-icon">{item.icon}</span>
			<span className="sidebar-nav-item__label">{item.label}</span>
			{hasChildren &&
				(isExpanded ? (
					<ExpandLessIcon sx={{ fontSize: "1.125rem" }} />
				) : (
					<ExpandMoreIcon sx={{ fontSize: "1.125rem" }} />
				))}
		</button>
	);

	return (
		<li key={item.key}>
			<div className="relative">
				{level === 0 ? (
					buttonContent
				) : (
					<Tooltip
						title={item.tooltip?.trim() || item.label}
						arrow
						placement="right"
						enterDelay={300}
						componentsProps={sidebarMenuTooltipComponentsProps}>
						<span className="block w-full min-w-0">{buttonContent}</span>
					</Tooltip>
				)}
			</div>
			{hasChildren && isExpanded && renderChildren && (
				<ul className="mt-0 space-y-0">{renderChildren(item.children || [], item.key)}</ul>
			)}
		</li>
	);
};

export default SidebarMenuItem;
